import { Injectable } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { Observable } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { BillingPortalSessionModel } from "src/app/models/billing-portal-session-model";
import { BillingPortalSessionService } from "src/app/services/billing-portal-session.service";
import { BillingPortalState } from "./billing-portal.state";

const initialState: BillingPortalState = {
    loading: false,
    loaded: false,
    success: false,
    errorMessage: null,
    billingPortalSession : null,
}

@Injectable({
    providedIn: 'root'
})
export class BillingPortalComponentStore extends ComponentStore<BillingPortalState> {
    constructor(private readonly billingPortalSessionService: BillingPortalSessionService) {
        super(initialState);
    }

    readonly billingPortalSession$: Observable<BillingPortalSessionModel> = this.select(state => state.billingPortalSession);
    readonly loading$: Observable<boolean> = this.select(state => state.loading);
    readonly loaded$: Observable<boolean> = this.select(state => state.loaded);
    readonly success$: Observable<boolean> = this.select(state => state.success);
    readonly errorMessage$: Observable<any> = this.select(state => state.errorMessage);

    readonly viewModel$ = this.select(
        this.billingPortalSession$,
        this.loaded$,
        this.loading$,
        this.success$,
        this.errorMessage$,
        (billingPortalSession, loaded, loading, success, errorMessage) => ({
            billingPortalSession, loaded, loading, success, errorMessage
        })
    );

    readonly setInitial = this.updater(( _ : BillingPortalState) => {
        return {
            ...initialState
        };
    });

    readonly setLoading = this.updater((state: BillingPortalState) => {
        return {
            ...state,
            loading: true,
            loaded: false,
        };
    });

    readonly setLoaded = this.updater((state: BillingPortalState) => {
        return {
            ...state,
            loading: false,
            loaded: true,
        };
    });

    readonly updateState = this.updater((state: BillingPortalState, value: {
        billingPortalSession: BillingPortalSessionModel
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: true,
            billingPortalSession: value.billingPortalSession
        };
    });

    readonly setError = this.updater((state: BillingPortalState, value: {
        errorMessage: String
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: false,
            errorMessage: value.errorMessage,
        };
    });

    readonly getBillingPortalSession = this.effect((detailParams$: Observable<{ companyId: string }>) => {
        this.setLoading();
        return detailParams$.pipe(
            switchMap((params => this.billingPortalSessionService.getBillingPortalSession(params.companyId).pipe(
                map((paymentPortalSession) => {
                    this.updateState({ billingPortalSession: paymentPortalSession });
                    this.setLoaded();
                }
                ),
                catchError(async (error) => {
                    this.setError({ errorMessage: error });
                    this.setLoaded();
                })
            ))
            )
        );
    });
}